import {
  ComercializadorSchema,
  ConsultaDeCertificadosSchema,
  ListaDeCertificadosSchema,
  OpcionesDeColumnaSchema,
  type ConsultaDeCertificados,
  PanelDeCertificadosSchema,
  PropuestaDeExportacionSchema,
  PropuestaDeRecoleccionSchema,
  ResultadoDeCargaDeCertificadosSchema,
  ResultadoDeCertificadosDeExportacionSchema,
  ResultadoDeCertificadosDeRecoleccionSchema,
  type PeticionDeCertificadosDeExportacion,
  ResultadoDeEliminarCertificadosSchema,
  ResultadoDeCertificadoPorFueraSchema,
  type PeticionDeCertificadoPorFuera,
  PaqueteDeCertificadosSchema,
  type DocumentoDeOrigen,
  type PeticionDeEliminarCertificados,
  type PeticionDeCertificadosDeRecoleccion,
  type Comercializador,
  type PerfilDePlanta,
  type PerfilDeTitulo,
  type TipoDeCargaDeCertificados,
} from '@cnc/contracts';
import {keepPreviousData, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {json, pedir} from './cliente';
import {aParametros} from './consulta';

// Certificados de origen y de analisis (decision 0067).
export function usePanelDeCertificados() {
  return useQuery({
    queryKey: ['certificados', 'panel'],
    queryFn: () => pedir('certificados/panel', PanelDeCertificadosSchema),
  });
}

// La consulta de la lista, con sus filtros (decision 0076); vive en la direccion.
export const CONSULTA_DE_CERTIFICADOS_POR_DEFECTO = ConsultaDeCertificadosSchema.parse({});
export const parametrosDeCertificados = (consulta: Partial<ConsultaDeCertificados>) =>
  aParametros(consulta, CONSULTA_DE_CERTIFICADOS_POR_DEFECTO);

export function useListaDeCertificados(consulta: ConsultaDeCertificados) {
  const cadena = aParametros(consulta, {}).toString();
  return useQuery({
    queryKey: ['certificados', 'lista', cadena],
    queryFn: () => pedir(`certificados?${cadena}`, ListaDeCertificadosSchema),
    placeholderData: keepPreviousData,
  });
}

// Valores de una columna para su filtro; solo se piden al abrir la lista.
export function useOpcionesDeColumnaDeCertificados(
  consulta: ConsultaDeCertificados,
  columna: string,
  contiene: string,
  habilitado: boolean,
) {
  const parametros = aParametros({...consulta, limite: undefined}, {});
  parametros.set('columna', columna);
  if (contiene) parametros.set('contiene', contiene);
  const cadena = parametros.toString();
  return useQuery({
    queryKey: ['certificados', 'opciones-de-columna', cadena],
    queryFn: () => pedir(`certificados/opciones-de-columna?${cadena}`, OpcionesDeColumnaSchema),
    enabled: habilitado,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

function useCambio<A, R>(fn: (a: A) => Promise<R>) {
  const cliente = useQueryClient();
  return useMutation({mutationFn: fn, onSettled: () => cliente.invalidateQueries({queryKey: ['certificados']})});
}

export const useGuardarPerfilDeTitulo = () =>
  useCambio(({id, perfil}: {id: number; perfil: PerfilDeTitulo}) =>
    pedir(`certificados/titulos/${id}/perfil`, PanelDeCertificadosSchema, json('PUT', {perfil})),
  );

export const useGuardarPerfilDePlanta = () =>
  useCambio(({id, perfil}: {id: number; perfil: PerfilDePlanta}) =>
    pedir(`certificados/plantas/${id}/perfil`, PanelDeCertificadosSchema, json('PUT', {perfil})),
  );

export const useGuardarComercializador = () =>
  useCambio((datos: Comercializador) =>
    pedir('certificados/comercializador', ComercializadorSchema, json('PUT', datos)),
  );

export const useCargarCertificados = () =>
  useCambio(
    ({tipo, archivos, modo}: {tipo: TipoDeCargaDeCertificados; archivos: File[]; modo: 'revisar' | 'aplicar'}) => {
      const cuerpo = new FormData();
      for (const a of archivos) cuerpo.append('archivos', a, a.name);
      cuerpo.append('tipo', tipo);
      cuerpo.append('modo', modo);
      return pedir('certificados/cargar', ResultadoDeCargaDeCertificadosSchema, {method: 'POST', body: cuerpo});
    },
  );

// Leer las facturas en PDF que ya estan en los expedientes (decision 0068).
export const useLeerFacturasDeExpedientes = () =>
  useCambio((modo: 'revisar' | 'aplicar') =>
    pedir('certificados/facturas/leer-expedientes', ResultadoDeCargaDeCertificadosSchema, json('POST', {modo})),
  );

export const RUTA_RELACION_POR_BIG_BAG = '/api/certificados/relacion-por-big-bag';

// ── Generar (decision 0072) ─────────────────────────────────────────────────

export const rutaDeDocumentoDeOrigen = (id: number, documento: DocumentoDeOrigen) =>
  `/api/certificados/origen/${id}/archivo?documento=${documento}`;
export const rutaDeDocumentoDeAnalisis = (id: number) => `/api/certificados/analisis/${id}/archivo`;

export function usePropuestaDeRecoleccion(recoleccion: string | null) {
  return useQuery({
    queryKey: ['certificados', 'recoleccion', recoleccion],
    queryFn: () =>
      pedir(`certificados/recolecciones/${encodeURIComponent(recoleccion!)}`, PropuestaDeRecoleccionSchema),
    enabled: Boolean(recoleccion),
    retry: false,
  });
}

// Paquete de certificados de una exportacion (decision 0075).
export function usePaqueteDeCertificados(exportacion: string | null) {
  return useQuery({
    queryKey: ['certificados', 'paquete', exportacion],
    queryFn: () =>
      pedir(`certificados/exportaciones/${encodeURIComponent(exportacion!)}/paquete`, PaqueteDeCertificadosSchema),
    enabled: Boolean(exportacion),
    retry: false,
  });
}
export function rutaDelPaqueteDeCertificados(exportacion: string, fecha: string, radicado: string) {
  const p = new URLSearchParams();
  if (fecha) p.set('fecha', fecha);
  if (radicado.trim()) p.set('radicado', radicado.trim());
  const consulta = p.toString();
  return `/api/certificados/exportaciones/${encodeURIComponent(exportacion)}/paquete/archivo${consulta ? `?${consulta}` : ''}`;
}

// Eliminar los certificados de una recoleccion o de una exportacion (decision 0074).
export const useEliminarCertificados = () =>
  useCambio((peticion: PeticionDeEliminarCertificados) =>
    pedir('certificados/eliminar', ResultadoDeEliminarCertificadosSchema, json('POST', peticion)),
  );

// Un certificado hecho por fuera de la app (decision 0097).
export const useRegistrarCertificadoPorFuera = () =>
  useCambio((peticion: PeticionDeCertificadoPorFuera) =>
    pedir('certificados/por-fuera', ResultadoDeCertificadoPorFueraSchema, json('POST', peticion)),
  );

export const useCrearCertificadosDeRecoleccion = () =>
  useCambio(({recoleccion, peticion}: {recoleccion: string; peticion: PeticionDeCertificadosDeRecoleccion}) =>
    pedir(
      `certificados/recolecciones/${encodeURIComponent(recoleccion)}`,
      ResultadoDeCertificadosDeRecoleccionSchema,
      json('POST', peticion),
    ),
  );

// Todos los certificados que le faltan a una exportacion (decision 0088).
export function usePropuestaDeExportacion(exportacion: string | null) {
  return useQuery({
    queryKey: ['certificados', 'propuesta-exportacion', exportacion],
    queryFn: () =>
      pedir(`certificados/exportaciones/${encodeURIComponent(exportacion!)}/propuesta`, PropuestaDeExportacionSchema),
    enabled: Boolean(exportacion),
    retry: false,
  });
}

export const useCrearCertificadosDeExportacion = () =>
  useCambio(({exportacion, peticion}: {exportacion: string; peticion: PeticionDeCertificadosDeExportacion}) =>
    pedir(
      `certificados/exportaciones/${encodeURIComponent(exportacion)}/crear`,
      ResultadoDeCertificadosDeExportacionSchema,
      json('POST', peticion),
    ),
  );
